/**
 * Dashboard Filters Context
 * Manages global filter state across all dashboard tabs
 * - Date range filtering
 * - Competitor selection
 * - Filter synchronization
 */
import React, { createContext, useContext, useState, useCallback } from 'react';
import { subDays, format } from 'date-fns';

const DashboardFiltersContext = createContext(null);

export function DashboardFiltersProvider({ children }) {
  // Date range filter (default: last 7 days)
  const [dateRange, setDateRange] = useState({
    from: subDays(new Date(), 6),
    to: new Date(),
  });

  // Selected competitors for filtering (empty = show all)
  const [selectedCompetitors, setSelectedCompetitors] = useState([]);

  // Time range preset (7d, 14d, 30d, custom)
  const [timeRangePreset, setTimeRangePreset] = useState('7d');

  // Update date range with preset
  const updateDateRangePreset = useCallback((preset) => {
    setTimeRangePreset(preset);
    const to = new Date();
    let from;

    switch (preset) {
      case '7d':
        from = subDays(to, 6);
        break;
      case '14d':
        from = subDays(to, 13);
        break;
      case '30d':
        from = subDays(to, 29);
        break;
      case '90d':
        from = subDays(to, 89);
        break;
      default:
        return; // Custom - don't update
    }

    setDateRange({ from, to });
  }, []);

  // Update custom date range
  const updateDateRange = useCallback((range) => {
    setDateRange(range);
    setTimeRangePreset('custom');
  }, []);

  // Toggle competitor selection
  const toggleCompetitor = useCallback((competitorId) => {
    setSelectedCompetitors(prev => {
      if (prev.includes(competitorId)) {
        return prev.filter(id => id !== competitorId);
      }
      return [...prev, competitorId];
    });
  }, []);

  // Clear all competitor filters
  const clearCompetitorFilters = useCallback(() => {
    setSelectedCompetitors([]);
  }, []);

  // Select only specific competitors
  const setCompetitorFilter = useCallback((competitorIds) => {
    setSelectedCompetitors(competitorIds);
  }, []);

  // Reset all filters
  const resetFilters = useCallback(() => {
    setDateRange({
      from: subDays(new Date(), 6),
      to: new Date(),
    });
    setSelectedCompetitors([]);
    setTimeRangePreset('7d');
  }, []);

  // Get formatted date range for API calls
  const getFormattedDateRange = useCallback(() => {
    return {
      startDate: format(dateRange.from, 'yyyy-MM-dd'),
      endDate: format(dateRange.to, 'yyyy-MM-dd'),
    };
  }, [dateRange]);

  const value = {
    // State
    dateRange,
    selectedCompetitors,
    timeRangePreset,

    // Actions
    updateDateRange,
    updateDateRangePreset,
    toggleCompetitor,
    clearCompetitorFilters,
    setCompetitorFilter,
    resetFilters,
    getFormattedDateRange,
  };

  return (
    <DashboardFiltersContext.Provider value={value}>
      {children}
    </DashboardFiltersContext.Provider>
  );
}

export function useDashboardFilters() {
  const context = useContext(DashboardFiltersContext);
  if (!context) {
    throw new Error('useDashboardFilters must be used within a DashboardFiltersProvider');
  }
  return context;
}

export default DashboardFiltersContext;
